/**
 * Admin Utilities - Detachable Admin Module
 *
 * This file contains utility functions specific to the admin module.
 * When extracting to a separate app, this entire file can be moved.
 */

import type { PlatformUser, CreditTransaction, CreditEntry } from '../types';
import { formatCost } from '../../utils/formatCost';

// ================================
// USER UTILITIES
// ================================

/**
 * Check if a user has admin role
 */
export const isAdminUser = (user: { roles?: string[] } | null): boolean => {
  return user?.roles?.includes('admin') ?? false;
};

/**
 * Get user's full name or fallback to email/username
 */
export const getUserDisplayName = (user: Partial<PlatformUser>): string => {
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  if (user.firstName) {
    return user.firstName;
  }
  if (user.username) {
    return user.username;
  }
  return user.email || 'Unknown User';
};

/**
 * Get user's initials for avatar
 */
export const getUserInitials = (user: Partial<PlatformUser>): string => {
  if (user.firstName && user.lastName) {
    return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
  }
  if (user.firstName) {
    return user.firstName[0].toUpperCase();
  }
  if (user.username) {
    return user.username.slice(0, 2).toUpperCase();
  }
  if (user.email) {
    return user.email.slice(0, 2).toUpperCase();
  }
  return 'U';
};

/**
 * Format user status for display
 */
export const formatUserStatus = (user: PlatformUser): {
  text: string;
  color: string;
} => {
  if (!user.enabled) {
    return {
      text: 'Disabled',
      color: 'text-red-600 bg-red-100',
    };
  }
  
  if (!user.emailVerified) {
    return {
      text: 'Unverified',
      color: 'text-yellow-600 bg-yellow-100',
    };
  }
  
  return {
    text: 'Enabled',
    color: 'text-green-600 bg-green-100',
  };
};

// ================================
// CREDIT UTILITIES
// ================================

/**
 * Format currency amount using centralized utility
 * Now respects VITE_COST_DECIMAL_PLACES and rounds up
 */
export const formatCurrency = (amount: number | string | null | undefined): string => {
  return formatCost(amount);
};

/**
 * Format credit amount with currency
 */
export const formatCredits = (amount: number | string | null | undefined): string => {
  // Handle null, undefined, or non-numeric values
  if (amount === null || amount === undefined || amount === '') {
    return '$0.00';
  }
  
  // Convert to number if it's a string
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
  
  // Handle NaN or invalid numbers
  if (isNaN(numAmount) || !isFinite(numAmount)) {
    console.warn('formatCredits received invalid amount:', amount, 'type:', typeof amount);
    return '$0.00';
  }
  
  if (numAmount >= 1000000) {
    return `$${(numAmount / 1000000).toFixed(1)}M`;
  }
  if (numAmount >= 1000) {
    return `$${(numAmount / 1000).toFixed(1)}K`;
  }
  return `$${numAmount.toFixed(2)}`;
};

/**
 * Get credit type display info
 */
export const getCreditTypeInfo = (type: string): {
  label: string;
  color: string;
} => {
  switch (type.toLowerCase()) {
    case 'free_platform':
      return {
        label: 'Free Credits',
        color: 'bg-blue-100 text-blue-800',
      };
    case 'monthly':
      return {
        label: 'Monthly Credits',
        color: 'bg-green-100 text-green-800',
      };
    case 'paid_platform':
      return {
        label: 'Paid Credits',
        color: 'bg-yellow-100 text-yellow-800',
      };
    default:
      return {
        label: 'Credits',
        color: 'bg-gray-100 text-gray-800',
      };
  }
};

/**
 * Calculate remaining/total credits format from credit entries
 * Returns format like "$2.964040/$4.000000" similar to user frontend
 * Note: Negative remaining values are clamped to $0.000000 for display
 */
export const formatRemainingTotalCredits = (entries: any[], creditType: string, creditSummary?: any): string => {
  const creditTypeField = `${creditType}_credits`; // e.g., "free_platform_credits"
  const creditData = creditSummary?.[creditTypeField];
  
  // Handle both nested object format and simple number format
  let remaining = 0;
  let original = 0;
  
  if (typeof creditData === 'object' && creditData !== null) {
    // Nested format: { remaining: 2.964, original: 4.0 }
    remaining = creditData.remaining || 0;
    original = creditData.original || 0;
  } else {
    // Simple number format: 2.964
    remaining = creditData || 0;
    
    // Calculate original from entries
    if (entries && entries.length > 0) {
      const typeEntries = entries.filter(entry => entry.type === creditType);
      original = typeEntries.reduce((sum, entry) => sum + (entry.original_amount || 0), 0);
    }
    
    // If no entries but we have remaining, use remaining as original
    if (original === 0 && remaining > 0) {
      original = remaining;
    }
  }

  // Clamp negative remaining values to 0 for display
  // The debt is already shown in the "Current Plan" section, so we don't need to show it here
  const displayRemaining = Math.max(0, remaining);

  return `${formatCurrency(displayRemaining)}/${formatCurrency(original)}`;
};

/**
 * Helper function to get credit amount from summary for a specific type
 */
const getSummaryAmountForType = (creditSummary: any, creditType: string): number => {
  switch (creditType) {
    case 'free_platform':
      return Number(creditSummary.free_platform_credits) || 0;
    case 'monthly':
      return Number(creditSummary.monthly_credits) || 0;
    case 'paid_platform':
      return Number(creditSummary.paid_platform_credits) || 0;
    default:
      return 0;
  }
};

/**
 * Calculate credit usage percentage
 */
export const calculateCreditUsage = (entry: CreditEntry): number => {
  if (entry.original_amount === 0) return 0;
  return ((entry.original_amount - entry.remaining_amount) / entry.original_amount) * 100;
};

/**
 * Get credit usage color based on percentage
 */
export const getCreditUsageColor = (percentage: number): string => {
  if (percentage >= 90) return 'text-red-600';
  if (percentage >= 70) return 'text-yellow-600';
  if (percentage >= 50) return 'text-blue-600';
  return 'text-green-600';
};

// ================================
// PLAN UTILITIES
// ================================

/**
 * Get plan type display info
 */
export const getPlanInfo = (planType: string): {
  label: string;
  color: string;
  description: string;
} => {
  switch (planType.toLowerCase()) {
    case 'starter':
      return {
        label: 'Starter',
        color: 'bg-gray-100 text-gray-800',
        description: 'Basic plan with limited features'
      };
    case 'monthly':
      return {
        label: 'Monthly',
        color: 'bg-green-100 text-green-800',
        description: 'Monthly credit allowance'
      };
    case 'unlimited':
      return {
        label: 'Unlimited',
        color: 'bg-purple-100 text-purple-800',
        description: 'Unlimited access to all features'
      };
    default:
      return {
        label: planType,
        color: 'bg-gray-100 text-gray-800',
        description: 'Custom plan'
      };
  }
};

// ================================
// DATE UTILITIES
// ================================

/**
 * Format timestamp to readable date
 */
export const formatDate = (timestamp: number): string => {
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Format timestamp to relative time
 */
export const formatRelativeTime = (timestamp: number): string => {
  const now = Date.now();
  const diff = now - (timestamp * 1000);
  
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 30) return `${days}d ago`;
  
  return formatDate(timestamp);
};

/**
 * Check if timestamp is recent (within last 24 hours)
 */
export const isRecent = (timestamp: number): boolean => {
  const now = Date.now();
  const diff = now - (timestamp * 1000);
  return diff < (24 * 60 * 60 * 1000); // 24 hours in milliseconds
};

// ================================
// TRANSACTION UTILITIES
// ================================

/**
 * Format transaction type like user frontend
 */
export const formatTransactionType = (transactionType: string): string => {
  const types: Record<string, string> = {
    'initial_signup': 'Welcome Bonus',
    'monthly_reset': 'Monthly Plan Reset',
    'admin_free_credits': 'Admin Credit Grant',
    'admin_adjustment': 'Admin Adjustment',
    'admin_grant': 'Admin Grant',
    'payment_purchase': 'Credit Purchase',
    'refund': 'Refund',
    'bonus': 'Bonus Credit',
    'usage_deduction': 'Usage'
  };
  return types[transactionType] || transactionType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Get transaction color like user frontend
 */
export const getTransactionColor = (transactionType: string, amount: number): string => {
  if (amount > 0) {
    return 'text-green-600';
  } else {
    return 'text-red-600';
  }
};

/**
 * Get transaction type display info
 */
export const getTransactionTypeInfo = (type: string): {
  label: string;
  color: string;
} => {
  switch (type.toLowerCase()) {
    case 'admin_grant':
      return {
        label: 'Admin Grant',
        color: 'bg-blue-100 text-blue-800',
      };
    case 'usage_deduction':
      return {
        label: 'Usage',
        color: 'bg-red-100 text-red-800',
      };
    case 'signup_bonus':
      return {
        label: 'Signup Bonus',
        color: 'bg-green-100 text-green-800',
      };
    case 'monthly_reset':
      return {
        label: 'Monthly Reset',
        color: 'bg-purple-100 text-purple-800',
      };
    case 'purchase':
      return {
        label: 'Purchase',
        color: 'bg-yellow-100 text-yellow-800',
      };
    default:
      return {
        label: type,
        color: 'bg-gray-100 text-gray-800',
      };
  }
};

/**
 * Sort transactions by date (newest first)
 */
export const sortTransactionsByDate = (transactions: CreditTransaction[]): CreditTransaction[] => {
  return [...transactions].sort((a, b) => new Date(b.created_at * 1000).getTime() - new Date(a.created_at * 1000).getTime());
};

// ================================
// VALIDATION UTILITIES
// ================================

/**
 * Validate email format
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate credit amount
 */
export const isValidCreditAmount = (amount: string | number): boolean => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return !isNaN(num) && num > 0 && num <= 10000; // Max $10,000
};

/**
 * Validate user identifier (email, username, or UUID)
 */
export const isValidUserIdentifier = (identifier: string): boolean => {
  if (!identifier || identifier.trim().length === 0) return false;
  
  // Check if it's an email
  if (isValidEmail(identifier)) return true;
  
  // Check if it's a UUID
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  if (uuidRegex.test(identifier)) return true;
  
  // Check if it's a valid username
  const usernameRegex = /^[a-zA-Z0-9._-]+$/;
  return usernameRegex.test(identifier) && identifier.length >= 3;
};

// ================================
// SEARCH UTILITIES
// ================================

/**
 * Filter users based on search query
 */
export const filterUsers = (users: PlatformUser[], query: string): PlatformUser[] => {
  if (!query.trim()) return users;
  
  const searchTerm = query.toLowerCase();
  return users.filter(user => 
    user.email.toLowerCase().includes(searchTerm) ||
    user.username?.toLowerCase().includes(searchTerm) ||
    user.firstName?.toLowerCase().includes(searchTerm) ||
    user.lastName?.toLowerCase().includes(searchTerm) ||
    getUserDisplayName(user).toLowerCase().includes(searchTerm)
  );
};

/**
 * Highlight search terms in text
 */
export const highlightSearchTerm = (text: string, searchTerm: string): string => {
  if (!searchTerm.trim()) return text;
  
  const regex = new RegExp(`(${searchTerm})`, 'gi');
  return text.replace(regex, '<mark class="bg-yellow-200">$1</mark>');
};

// ================================
// EXPORT UTILITIES
// ================================

/**
 * Export data to CSV format
 */
export const exportToCSV = (data: any[], filename: string): void => {
  if (data.length === 0) return;
  
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        // Escape commas and quotes in CSV
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',')
    )
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// ================================
// LOCAL STORAGE UTILITIES
// ================================

/**
 * Save admin preferences to localStorage
 */
export const saveAdminPreferences = (preferences: Record<string, any>): void => {
  try {
    localStorage.setItem('adminPreferences', JSON.stringify(preferences));
  } catch (error) {
    console.warn('Failed to save admin preferences:', error);
  }
};

/**
 * Load admin preferences from localStorage
 */
export const loadAdminPreferences = (): Record<string, any> => {
  try {
    const stored = localStorage.getItem('adminPreferences');
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.warn('Failed to load admin preferences:', error);
    return {};
  }
};

// ================================
// DEBOUNCE UTILITY
// ================================

/**
 * Debounce function for search inputs
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

